import { useState, useEffect, memo } from 'react';
import { Tag, Users, Inbox, CheckCircle, Mail, AlertCircle, Clock, Star } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { toggleChatLabelThunk } from '@common-reducers/labels-reducer/labels-thunks';
import { selectAssignableDedupedLabels } from '@common-reducers/labels-reducer/selectors';
import { contactsLabelsSelectByQuery } from '@common-reducers/DBServiceThunks';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from "@common-config/constants";

interface LabelsManagerButtonProps {
    waChatId: string;
    size?: number;
    className?: string;
    iconClassName?: string;
}

// Helper to get label icon (copied from top-header-label-item.tsx)
const getLabelIcon = (label: any, className: string = "w-4 h-4 shrink-0") => {
    const iconProps = { className };

    switch (label.name.toLowerCase()) {
        case 'all':
            return <Users {...iconProps} />;
        case 'inbox zero':
            return <Inbox {...iconProps} />;
        case 'done':
            return <CheckCircle {...iconProps} />;
        case 'unread':
            return <Mail {...iconProps} />;
        case 'needs attention':
            return <AlertCircle {...iconProps} />;
        case 'snoozed':
            return <Clock {...iconProps} />;
        case 'starred':
            return <Star {...iconProps} />;

    }

    if (label.icon && label.icon.trim()) {
        const DynamicIcon = (LucideIcons as any)[label.icon];
        if (DynamicIcon) {
            return <DynamicIcon {...iconProps} />;
        }
        return <span className={cn("text-lg leading-none shrink-0", className)}>{label.icon}</span>;
    }

    // Default fallback if no icon
    return <Tag {...iconProps} />;
};

export default memo(function LabelsManagerButton({ waChatId, size = 20, className, iconClassName }: LabelsManagerButtonProps) {
    const { t, i18n } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const localPortalContainer = usePortalContainer();
    const [portalContainer, setPortalContainer] = useState<HTMLElement | ShadowRoot | null>(null);
    const isDarkMode = useAppSelector((state) => state.UserReducer.whatsappDarkMode);
    const isRtl = i18n.dir(i18n.language) === 'rtl';

    const systemLabelNames = ['all', 'inbox', 'inbox zero', 'handle later', 'needs attention', 'unread', 'starred', 'done', 'snoozed'];

    const getTranslatedLabelName = (labelName: string, labelType?: string) => {
        if (labelType === 'default' || systemLabelNames.includes(labelName.toLowerCase())) {
            const key = `labels.systemLabels.${labelName.toLowerCase()}`;
            const translated = t(key);
            return translated !== key ? translated : labelName;
        }
        return labelName;
    };

    useEffect(() => {
        // Try to find the global portal container (LabelsModals) to avoid z-index/overflow issues in chat rows
        // This is necessary because rows in virtual lists often have stacking contexts that trap absolute elements
        const globalPortal = document.getElementById('labels-modals')?.shadowRoot?.querySelector('.btcss-portal-container');
        if (globalPortal) {
            setPortalContainer(globalPortal as HTMLElement);
        }
    }, []);

    // Get all assignable labels (de-duplicated + ordered via a memoized selector; #164).
    const allLabels = useAppSelector(selectAssignableDedupedLabels);

    // Get all labels assigned to this contact
    const contactLabels = useAppSelector(state => contactsLabelsSelectByQuery(state.DBContactsLabelsReducer, {
        waChatId
    }));

    const handleToggleLabel = (label: any) => {
        dispatch(toggleChatLabelThunk({
            waChatId: waChatId,
            labelId: label._id,
        }));
    };

    return (
        <DropdownMenu>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className={cn("p-1 rounded transition-colors text-gray-500 hover:text-blue-500 outline-none data-[state=open]:text-blue-500 cursor-pointer", className)}
                                >
                                    <Tag size={size} className={cn("currentColor", iconClassName)} strokeWidth={2} />
                                </button>
                            </DropdownMenuTrigger>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                        <p>{t('labels.assignLabels')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>

            <DropdownMenuContent
                align="end"
                container={portalContainer || localPortalContainer}
                className="w-56 z-[9999]"
                sideOffset={6}
                // Force stable colors in dark mode (avoids transparent/background bleed from host styles)
                style={{
                    direction: isRtl ? 'rtl' : 'ltr',
                    // Use hardcoded colors to avoid collisions with WhatsApp CSS variables.
                    backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
                    color: isDarkMode ? '#f1f5f9' : '#111827',
                    borderColor: isDarkMode ? '#334155' : '#d1d5db',
                    boxShadow: isDarkMode ? '0 16px 40px rgba(2, 6, 23, 0.55)' : '0 12px 30px rgba(15, 23, 42, 0.15)',
                    backdropFilter: 'none',
                    opacity: 1,
                }}
            >
                {allLabels
                    .filter(label => label.name?.toLowerCase() !== 'all' && label.name?.toLowerCase() !== 'unread' && label.name?.toLowerCase() !== 'inbox zero')
                    .map((label) => {
                        // Find the Done label ID securely
                        const doneLabelDef = allLabels.find(l => l.name?.toLowerCase() === 'done');
                        const hasDoneLabel = contactLabels.some(cl => cl.labelId === doneLabelDef?._id);

                        const isInboxZeroLabel = label.name?.toLowerCase() === 'inbox zero';

                        let isChecked = contactLabels.some(cl => cl.labelId === label._id);
                        let onToggle = () => handleToggleLabel(label);

                        if (isInboxZeroLabel) {
                            // Inbox Zero is checked if getting things done (no done label)
                            isChecked = !hasDoneLabel;
                            onToggle = () => {
                                // If clicking "Inbox Zero", we ensure "Done" is removed.
                                if (hasDoneLabel && doneLabelDef) {
                                    handleToggleLabel(doneLabelDef);
                                }
                            };
                        }

                        return (
                            <DropdownMenuCheckboxItem
                                key={label._id}
                                checked={isChecked}
                                onCheckedChange={onToggle}
                                disabled={isInboxZeroLabel && isChecked}
                                className="cursor-pointer"
                            >
                                {getLabelIcon(label, "w-4 h-4 shrink-0")}
                                {getTranslatedLabelName(label.name ?? '', label.type)}
                            </DropdownMenuCheckboxItem>
                        );
                    })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
})
